import { useState, useEffect } from 'react';
import { updateItem, FIG_LISTS } from '../../api/figApi';
import generateACORD140 from './generateACORD140';
import { parseLocalDate } from '../../utils/parseLocalDate';
import './FIG.css';

// Find a renewal policy for `current` — same InsuredId + LOB with a later
// EffectiveDate. Returns the renewal's EffectiveDate string, or '' if none.
function findRenewalEffectiveDate(current, allPolicies) {
  if (!current?.EffectiveDate || !current?.LOB) return '';
  const currentDt = parseLocalDate(current.EffectiveDate);
  if (!currentDt) return '';
  const later = (allPolicies || [])
    .filter(p => p.id !== current.id && p.LOB === current.LOB)
    .map(p => ({ p, dt: parseLocalDate(p.EffectiveDate) }))
    .filter(x => x.dt && x.dt > currentDt)
    .sort((a, b) => b.dt - a.dt);
  const eff = later[0]?.p?.EffectiveDate;
  if (!eff) return '';
  // Normalise to the ACORD's MM/DD/YYYY regardless of how the row stores it.
  const dt = parseLocalDate(eff);
  return dt ? `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}` : eff;
}

// ACORD premises / building numbering from FIG_Buildings rows: each distinct
// street address is a premises (in first-seen order) and buildings number
// sequentially within their premises. Green Span: 21200 FM 362 → Premises 1
// (Bldg 1 Plant 1, Bldg 2 Warehouse); 21270 FM 362 → Premises 2 / Bldg 1.
function premisesNumbering(bldgs) {
  const norm = (b) => `${b.Address || ''}|${b.City || ''}|${b.State || ''}|${b.Zip || ''}`.toLowerCase().replace(/\s+/g, ' ').trim();
  const premisesByAddr = new Map();
  const countByPremises = new Map();
  return bldgs.map((b) => {
    const key = norm(b);
    if (!premisesByAddr.has(key)) premisesByAddr.set(key, premisesByAddr.size + 1);
    const locNum = premisesByAddr.get(key);
    const bldgNum = (countByPremises.get(locNum) || 0) + 1;
    countByPremises.set(locNum, bldgNum);
    return { locNum: String(locNum), bldgNum: String(bldgNum) };
  });
}

const VALUATION_CODES = ['', 'ACV', 'RC', 'FV', 'AGR'];
const CAUSE_OF_LOSS = ['', 'Basic', 'Broad', 'Special'];
const CONSTRUCTION_TYPES = ['', 'Frame', 'Joisted Masonry', 'Non-Combustible', 'Masonry Non-Combustible', 'Modified Fire Resistive', 'Fire Resistive'];

function ACORD140({ insured, policy, policies, buildings, additionalInterests, getToken, onDirtyChange, onBack }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({});

  const p = policy || {};
  const bldgs = buildings || [];
  const interests = (additionalInterests || []).filter(ai =>
    ['loss payee', 'mortgagee', 'lenders loss payable'].includes((ai.InterestType || '').toLowerCase())
  );

  useEffect(() => {
    // Effective date is the renewal's date if a later-term policy exists on the
    // same LOB for this insured; otherwise blank (underwriter fills at submission).
    const renewalEff = findRenewalEffectiveDate(p, policies);
    // BI/EE row/checkbox is policy-gated: only include when BusinessIncome is
    // true AND the limit is > 0. Building-level values feed the row when present.
    const includeBIEE = !!p.BusinessIncome && Number(p.BusinessIncomeLimit) > 0;
    const opts = {
      policyDeductible: p.Deductible,
      includeBIEE,
    };
    const numbering = premisesNumbering(bldgs);
    setForm({
      date: new Date().toISOString().split('T')[0],
      carrier: p.Carrier || '', naicCode: p.NAICCode || '',
      policyNumber: p.PolicyNumber || '', effectiveDate: renewalEff,
      // Blanket summary (4 rows)
      blankets: [0,1,2,3].map(() => ({ num: '', amount: '', type: '' })),
      // One location per building — unbounded. Generator merges them into one PDF.
      locs: bldgs.map((b, i) => buildLocState(b, interests[i] || {}, numbering[i], opts)),
      remarks: '',
    });
    if (onDirtyChange) onDirtyChange(false);
  }, [policy?.id, bldgs.length, policies?.length]);

  function buildLocState(bldg, interest, numbering, opts = {}) {
    const { policyDeductible = '', includeBIEE = false } = opts;
    const hasDed = policyDeductible !== '' && policyDeductible != null;
    return {
      locNum: numbering?.locNum || '1', address: bldg.Address || '', bldgNum: numbering?.bldgNum || '1', bldgDesc: bldg.Title || '',
      // Coverage schedule (5 rows). Row 3 is BI/EE only when policy includes it.
      // TODO: FIG_Buildings needs a Valuation column — currently hardcoded 'RC'.
      //       Bldg 1 of DH Ranch (Dwelling) should be marked 'FBV' per binder.
      // BI/EE row: policy must include BI/EE AND this building must carry a
      // BusinessIncomeValue > 0 — otherwise the row stays blank.
      coverages: [0,1,2,3,4].map(i => {
        const bldgBI = includeBIEE && Number(bldg.BusinessIncomeValue) > 0;
        const defaults = [
          { subj: 'BLDG', limit: bldg.BuildingValue || '', coins: '80', val: 'RC', cause: 'Special' },
          { subj: 'BPP', limit: bldg.BPPValue || '', coins: '80', val: 'RC', cause: 'Special' },
          bldgBI
            ? { subj: 'BI/EE', limit: bldg.BusinessIncomeValue, coins: '', val: '', cause: 'Special' }
            : { subj: '', limit: '', coins: '', val: '', cause: '' },
          { subj: '', limit: '', coins: '', val: '', cause: '' },
          { subj: '', limit: '', coins: '', val: '', cause: '' },
        ][i];
        const subjPresent = !!defaults.subj;
        return {
          subj: defaults.subj, limit: defaults.limit, coins: defaults.coins, val: defaults.val,
          cause: defaults.cause, inflGuard: '',
          ded: subjPresent && hasDed ? String(policyDeductible) : '',
          dedType: subjPresent && hasDed ? 'AOP' : '',
          blanketNum: '', forms: '',
        };
      }),
      // "Attach ACORD 810" is never auto-checked — the 810 is a separate
      // decision, not implied by a BI/EE amount. User ticks it on the form.
      biExtraExpense: false, valueReporting: false,
      // Spoilage
      spoilage: 'N', spoilageDesc: '', spoilageLimit: '', spoilageDed: '', spoilageRefrig: 'N',
      spoilageBreakdown: false, spoilagePower: false, spoilageSelling: false, spoilageOther: false,
      optionsDesc: '',
      // Sinkhole / Mine — default blank so neither ACCEPT nor REJECT auto-checks.
      // Underwriter fills at submission, or caller supplies 'Y'/'N' explicitly.
      sinkhole: '', sinkholeLimit: '', mine: '', mineLimit: '',
      historicalProperty: false, openSides: '',
      // Construction
      constructionCode: bldg.ConstructionType || '', stories: bldg.Stories || '',
      basements: bldg.Basements || '', yearBuilt: bldg.YearBuilt || '', totalArea: bldg.SquareFootage || '',
      hydrantDist: bldg.DistanceToHydrant || '', fireStationDist: bldg.FireStationDist || '',
      fireDistrictName: '', fireDistrictCode: bldg.FireDistrictCode || '',
      protectionClass: bldg.PPCRating || '',
      bceg: '', taxCode: bldg.TaxCode || '', roofType: bldg.RoofType || '',
      // Occupancy belongs in BLDG DESCRIPTION (bldgDesc, from Title). OTHER
      // OCCUPANCIES stays blank unless entered on the form.
      otherOccupancies: '',
      // Improvements
      impWiring: !!bldg.ImprovementWiringYear, impWiringYr: bldg.ImprovementWiringYear || '',
      impRoofing: !!bldg.ImprovementRoofingYear, impRoofingYr: bldg.ImprovementRoofingYear || '',
      impPlumbing: !!bldg.ImprovementPlumbingYear, impPlumbingYr: bldg.ImprovementPlumbingYear || '',
      impHeating: !!bldg.ImprovementHeatingYear, impHeatingYr: bldg.ImprovementHeatingYear || '',
      impOther: !!bldg.ImprovementOtherYear, impOtherDesc: bldg.ImprovementOtherDesc || '', impOtherYr: bldg.ImprovementOtherYear || '',
      // Wind
      windClass: bldg.WindClass || '', solidFuelHeater: false, solidFuelMfg: '', solidFuelDate: '',
      // Heat
      primaryHeat: bldg.PrimaryHeatType || '', primaryHeatOther: '', primaryBoilerInsured: '',
      secondaryHeat: bldg.SecondaryHeatType || '', secondaryHeatOther: '', secondaryBoilerInsured: '',
      // Exposures
      expRight: bldg.RightExposure || '', expRightDist: '', expLeft: bldg.LeftExposure || '', expLeftDist: '',
      expFront: bldg.FrontExposure || '', expFrontDist: '', expRear: bldg.RearExposure || '', expRearDist: '',
      // Burglar alarm
      burglarType: bldg.BurglarAlarmType || '', burglarCert: bldg.BurglarAlarmCertNumber || '',
      burglarExp: bldg.BurglarAlarmExpDate || '', burglarInstaller: bldg.BurglarAlarmInstalledBy || '',
      burglarExtent: bldg.BurglarAlarmExtent || '', burglarGrade: bldg.BurglarAlarmGrade || '',
      // Security
      guardCount: bldg.GuardsWatchmen || '', guardType: bldg.ClockHourly || '',
      // Fire protection
      fireProtDesc: bldg.FireProtectionDesc || '', sprinklerPct: bldg.FireProtectionSprinklerPct || '',
      fireMfg: bldg.FireAlarmManufacturer || '',
      fireCentral: false, fireLocal: false,
      // Additional Interest
      interestType: interest.InterestType || '',
      interestName: interest.EntityName || '', interestAddress: interest.Address || '',
      interestCity: interest.City || '', interestState: interest.State || '', interestZip: interest.Zip || '',
      interestLoanNum: interest.ReferenceNumber || '',
      remarkText: bldg.BuildingDescription || '',
    };
  }

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
    if (onDirtyChange) onDirtyChange(true);
  }

  function setLoc(locIdx, key, val) {
    setForm(prev => {
      const locs = [...(prev.locs || [])];
      locs[locIdx] = { ...locs[locIdx], [key]: val };
      return { ...prev, locs };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  function setLocCov(locIdx, covIdx, key, val) {
    setForm(prev => {
      const locs = [...(prev.locs || [])];
      const loc = { ...locs[locIdx] };
      const coverages = [...(loc.coverages || [])];
      coverages[covIdx] = { ...coverages[covIdx], [key]: val };
      loc.coverages = coverages;
      locs[locIdx] = loc;
      return { ...prev, locs };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  function setBlanket(idx, key, val) {
    setForm(prev => {
      const blankets = [...prev.blankets];
      blankets[idx] = { ...blankets[idx], [key]: val };
      return { ...prev, blankets };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  function buildBldgPayload(loc) {
    return {
      // Address
      Address: loc.address,
      // Construction
      BuildingDescription: loc.bldgDesc,
      ConstructionType: loc.constructionCode,
      YearBuilt: loc.yearBuilt ? Number(loc.yearBuilt) : null,
      SquareFootage: loc.totalArea ? Number(loc.totalArea) : null,
      Stories: loc.stories ? Number(loc.stories) : null,
      Basements: loc.basements ? Number(loc.basements) : null,
      PPCRating: loc.protectionClass,
      RoofType: loc.roofType,
      TaxCode: loc.taxCode,
      WindClass: loc.windClass,
      // Distances & fire district
      DistanceToHydrant: loc.hydrantDist,
      FireStationDist: loc.fireStationDist,
      FireDistrictCode: loc.fireDistrictCode,
      // Improvements
      ImprovementWiringYear: loc.impWiringYr,
      ImprovementPlumbingYear: loc.impPlumbingYr,
      ImprovementRoofingYear: loc.impRoofingYr,
      ImprovementHeatingYear: loc.impHeatingYr,
      ImprovementOtherYear: loc.impOtherYr,
      ImprovementOtherDesc: loc.impOtherDesc,
      // Heating
      PrimaryHeatType: loc.primaryHeat,
      SecondaryHeatType: loc.secondaryHeat,
      // Exposures
      RightExposure: loc.expRight,
      LeftExposure: loc.expLeft,
      FrontExposure: loc.expFront,
      RearExposure: loc.expRear,
      // Burglar alarm
      BurglarAlarmType: loc.burglarType,
      BurglarAlarmCertNumber: loc.burglarCert,
      BurglarAlarmExpDate: loc.burglarExp,
      BurglarAlarmInstalledBy: loc.burglarInstaller,
      BurglarAlarmExtent: loc.burglarExtent,
      BurglarAlarmGrade: loc.burglarGrade,
      // Occupancy — only written when something was entered in OTHER
      // OCCUPANCIES; a blank form field must not wipe OccupancyType.
      ...(loc.otherOccupancies ? { OccupancyType: loc.otherOccupancies } : {}),
      // Security
      GuardsWatchmen: loc.guardCount,
      ClockHourly: loc.guardType,
      // Fire protection
      FireProtectionDesc: loc.fireProtDesc,
      FireProtectionSprinklerPct: loc.sprinklerPct,
      FireAlarmManufacturer: loc.fireMfg,
    };
  }

  async function handleSave() {
    if (!p.id || !getToken) return;
    setSaving(true); setSaveMsg('');
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.POLICIES, p.id, {
        Carrier: form.carrier, NAICCode: form.naicCode,
        PolicyNumber: form.policyNumber, EffectiveDate: form.effectiveDate,
      });
      const locs = form.locs || [];
      for (let i = 0; i < locs.length; i++) {
        const bldgId = bldgs[i]?.id;
        if (!bldgId) continue;
        const payload = buildBldgPayload(locs[i]);
        await updateItem(token, FIG_LISTS.BUILDINGS, bldgId, payload);
      }
      setSaveMsg('Saved');
      if (onDirtyChange) onDirtyChange(false);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) { setSaveMsg('Error: ' + err.message); }
    finally { setSaving(false); }
  }

  // Page nav — one page per location (minimum 2 location pages to preserve the
  // canonical Location A + Location B + Signature layout for single-building cases),
  // plus one signature page at the end.
  const locCount = (form.locs || []).length;
  const locPages = Math.max(locCount, 2);
  const totalPages = locPages + 1;
  const signaturePage = totalPages;
  const navBtnStyle = (disabled) => ({
    width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.1rem', fontWeight: 700, background: disabled ? '#ccc' : '#7eb8da',
    color: '#fff', border: 'none', borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1, fontFamily: 'inherit',
  });
  const pageNav = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center', margin: '0.75rem 0' }}>
      <button style={navBtnStyle(currentPage === 1)} disabled={currentPage === 1}
        onClick={() => setCurrentPage(pg => Math.max(1, pg - 1))}>&lsaquo;</button>
      <span style={{ fontSize: '0.8rem', color: '#666' }}>Page {currentPage} of {totalPages}</span>
      <button style={navBtnStyle(currentPage === totalPages)} disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(pg => Math.min(totalPages, pg + 1))}>&rsaquo;</button>
    </div>
  );

  // Render a full location page
  function renderLocation(locIdx, locLabel) {
    const loc = (form.locs || [])[locIdx] || {};
    const s = (key, val) => setLoc(locIdx, key, val);
    const sc = (idx, key, val) => setLocCov(locIdx, idx, key, val);

    return (<>
      {/* Premises */}
      <div className="acord-section">
        <div className="acord-section-bar">PREMISES — {locLabel}</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <label className="acord-cell acord-cell--xs"><span className="acord-label">LOC #</span>
              <input className="acord-input" value={loc.locNum} onChange={e => s('locNum', e.target.value)} /></label>
            <label className="acord-cell acord-cell--2"><span className="acord-label">STREET ADDRESS</span>
              <input className="acord-input" value={loc.address} onChange={e => s('address', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">BLDG #</span>
              <input className="acord-input" value={loc.bldgNum} onChange={e => s('bldgNum', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">BUILDING DESCRIPTION</span>
              <input className="acord-input" value={loc.bldgDesc} onChange={e => s('bldgDesc', e.target.value)} /></label>
          </div>
        </div>
      </div>

      {/* Coverage Schedule */}
      <div className="acord-section">
        <div className="acord-section-bar">COVERAGE SCHEDULE</div>
        <div className="acord-section-body" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead><tr style={{ background: '#eef2f7' }}>
              {['SUBJ', 'AMOUNT', 'COINS%', 'VAL', 'CAUSE', 'INF%', 'DED', 'DED TYPE', 'BLKT#', 'FORMS'].map(h => (
                <th key={h} style={{ padding: '2px 3px', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(loc.coverages || []).map((c, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.subj} onChange={e => sc(i, 'subj', e.target.value)} style={{ width: '3.5rem' }} /></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" type="number" value={c.limit} onChange={e => sc(i, 'limit', e.target.value)} style={{ width: '5rem' }} /></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.coins} onChange={e => sc(i, 'coins', e.target.value)} style={{ width: '2.5rem' }} /></td>
                  <td style={{ padding: '2px' }}><select className="acord-input" value={c.val} onChange={e => sc(i, 'val', e.target.value)} style={{ width: '3.5rem', fontSize: '0.7rem' }}>
                    {VALUATION_CODES.map(v => <option key={v} value={v}>{v || '—'}</option>)}</select></td>
                  <td style={{ padding: '2px' }}><select className="acord-input" value={c.cause} onChange={e => sc(i, 'cause', e.target.value)} style={{ width: '4rem', fontSize: '0.7rem' }}>
                    {CAUSE_OF_LOSS.map(v => <option key={v} value={v}>{v || '—'}</option>)}</select></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.inflGuard} onChange={e => sc(i, 'inflGuard', e.target.value)} style={{ width: '2.5rem' }} /></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.ded} onChange={e => sc(i, 'ded', e.target.value)} style={{ width: '4rem' }} /></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.dedType} onChange={e => sc(i, 'dedType', e.target.value)} style={{ width: '3rem' }} /></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.blanketNum} onChange={e => sc(i, 'blanketNum', e.target.value)} style={{ width: '2.5rem' }} /></td>
                  <td style={{ padding: '2px' }}><input className="acord-input" value={c.forms} onChange={e => sc(i, 'forms', e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="acord-row" style={{ marginTop: '0.25rem' }}>
            <div className="acord-cell" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
              <label className="acord-checkbox"><input type="checkbox" checked={loc.biExtraExpense} onChange={e => s('biExtraExpense', e.target.checked)} /><span>Business Income / Extra Expense</span></label>
              <label className="acord-checkbox"><input type="checkbox" checked={loc.valueReporting} onChange={e => s('valueReporting', e.target.checked)} /><span>Value Reporting</span></label>
            </div>
          </div>
        </div>
      </div>

      {/* Construction */}
      <div className="acord-section">
        <div className="acord-section-bar">CONSTRUCTION</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">CONSTRUCTION TYPE</span>
              <select className="acord-input" value={loc.constructionCode} onChange={e => s('constructionCode', e.target.value)}>
                {CONSTRUCTION_TYPES.map(v => <option key={v} value={v}>{v || '—'}</option>)}</select></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">STORIES</span>
              <input className="acord-input" value={loc.stories} onChange={e => s('stories', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">BSMTS</span>
              <input className="acord-input" value={loc.basements} onChange={e => s('basements', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">YR BUILT</span>
              <input className="acord-input" value={loc.yearBuilt} onChange={e => s('yearBuilt', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">TOTAL AREA</span>
              <input className="acord-input" value={loc.totalArea} onChange={e => s('totalArea', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--sm"><span className="acord-label">HYDRANT (ft)</span>
              <input className="acord-input" value={loc.hydrantDist} onChange={e => s('hydrantDist', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">FIRE STN (mi)</span>
              <input className="acord-input" value={loc.fireStationDist} onChange={e => s('fireStationDist', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">FIRE DISTRICT</span>
              <input className="acord-input" value={loc.fireDistrictName} onChange={e => s('fireDistrictName', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">CODE</span>
              <input className="acord-input" value={loc.fireDistrictCode} onChange={e => s('fireDistrictCode', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">PPC</span>
              <input className="acord-input" value={loc.protectionClass} onChange={e => s('protectionClass', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell acord-cell--xs"><span className="acord-label">BCEG</span>
              <input className="acord-input" value={loc.bceg} onChange={e => s('bceg', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">TAX CODE</span>
              <input className="acord-input" value={loc.taxCode} onChange={e => s('taxCode', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">ROOF TYPE</span>
              <input className="acord-input" value={loc.roofType} onChange={e => s('roofType', e.target.value)} /></label>
            <label className="acord-cell"><span className="acord-label">OTHER OCCUPANCIES</span>
              <input className="acord-input" value={loc.otherOccupancies} onChange={e => s('otherOccupancies', e.target.value)} /></label>
          </div>
        </div>
      </div>

      {/* Improvements */}
      <div className="acord-section">
        <div className="acord-section-bar">BUILDING IMPROVEMENTS</div>
        <div className="acord-section-body">
          <div className="acord-row">
            {[['impWiring','impWiringYr','Wiring'],['impRoofing','impRoofingYr','Roofing'],['impPlumbing','impPlumbingYr','Plumbing'],
              ['impHeating','impHeatingYr','Heating']].map(([cb,yr,lbl]) => (
              <div className="acord-cell acord-cell--sm" key={cb} style={{ flexDirection: 'row', gap: '0.25rem', alignItems: 'center' }}>
                <label className="acord-checkbox"><input type="checkbox" checked={loc[cb]} onChange={e => s(cb, e.target.checked)} /><span>{lbl}</span></label>
                <input className="acord-input" placeholder="Year" value={loc[yr]} onChange={e => s(yr, e.target.value)} style={{ width: '3rem' }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Exposures */}
      <div className="acord-section">
        <div className="acord-section-bar">EXPOSURES</div>
        <div className="acord-section-body">
          <div className="acord-row">
            {[['expRight','expRightDist','Right'],['expLeft','expLeftDist','Left'],
              ['expFront','expFrontDist','Front'],['expRear','expRearDist','Rear']].map(([desc,dist,lbl]) => (
              <div className="acord-cell acord-cell--sm" key={desc}>
                <span className="acord-label">{lbl}</span>
                <input className="acord-input" placeholder="Desc" value={loc[desc]} onChange={e => s(desc, e.target.value)} style={{ marginBottom: '2px' }} />
                <input className="acord-input" placeholder="Dist" value={loc[dist]} onChange={e => s(dist, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Interest */}
      <div className="acord-section">
        <div className="acord-section-bar">ADDITIONAL INTEREST</div>
        <div className="acord-section-body">
          <div className="acord-row">
            <label className="acord-cell acord-cell--2"><span className="acord-label">NAME</span>
              <input className="acord-input" value={loc.interestName} onChange={e => s('interestName', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">LOAN #</span>
              <input className="acord-input" value={loc.interestLoanNum} onChange={e => s('interestLoanNum', e.target.value)} /></label>
          </div>
          <div className="acord-row">
            <label className="acord-cell"><span className="acord-label">ADDRESS</span>
              <input className="acord-input" value={loc.interestAddress} onChange={e => s('interestAddress', e.target.value)} /></label>
            <label className="acord-cell acord-cell--sm"><span className="acord-label">CITY</span>
              <input className="acord-input" value={loc.interestCity} onChange={e => s('interestCity', e.target.value)} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">ST</span>
              <input className="acord-input" value={loc.interestState} onChange={e => s('interestState', e.target.value)} maxLength={2} /></label>
            <label className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
              <input className="acord-input" value={loc.interestZip} onChange={e => s('interestZip', e.target.value)} /></label>
          </div>
        </div>
      </div>
    </>);
  }

  return (
    <div className="acord-form">
      <div className="acord-header">
        <div className="acord-header-left">
          <span className="acord-logo">ACORD</span>
          <span className="acord-form-title">ACORD 140 — PROPERTY SECTION</span>
        </div>
        <div className="acord-header-right">
          <span style={{ fontSize: '0.8rem', color: '#ccc' }}>Page {currentPage} of {totalPages}</span>
        </div>
      </div>

      {/* PAGE 1 */}
      {currentPage === 1 && <>
        {/* Header */}
        <div className="acord-section">
          <div className="acord-section-bar">POLICY</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">PRODUCER</span><input className="acord-input" value="Fragos Insurance Group LLC" readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">POLICY #</span><input className="acord-input" value={form.policyNumber} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">CARRIER</span><input className="acord-input" value={form.carrier} readOnly /></div>
              <div className="acord-cell acord-cell--xs"><span className="acord-label">NAIC</span><input className="acord-input" value={form.naicCode} readOnly /></div>
              <div className="acord-cell"><span className="acord-label">EFF DATE</span><input className="acord-input" value={form.effectiveDate} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell acord-cell--2"><span className="acord-label">NAMED INSURED</span><input className="acord-input" value={insured?.CompanyName || ''} readOnly /></div>
            </div>
          </div>
        </div>

        {/* Blanket Summary */}
        <div className="acord-section">
          <div className="acord-section-bar">BLANKET SUMMARY</div>
          <div className="acord-section-body">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead><tr style={{ background: '#eef2f7' }}>
                <th style={{ padding: '2px 4px', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700 }}>BLANKET #</th>
                <th style={{ padding: '2px 4px', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700 }}>AMOUNT</th>
                <th style={{ padding: '2px 4px', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700 }}>TYPE</th>
              </tr></thead>
              <tbody>
                {(form.blankets || []).map((b, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={b.num} onChange={e => setBlanket(i, 'num', e.target.value)} style={{ width: '4rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" type="number" value={b.amount} onChange={e => setBlanket(i, 'amount', e.target.value)} style={{ width: '6rem' }} /></td>
                    <td style={{ padding: '2px 3px' }}><input className="acord-input" value={b.type} onChange={e => setBlanket(i, 'type', e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {renderLocation(0, 'LOCATION A')}
        {pageNav}
      </>}

      {/* PAGE 2 — Location B (+ Remarks) */}
      {currentPage === 2 && <>
        {renderLocation(1, 'LOCATION B')}
        <div className="acord-section">
          <div className="acord-section-bar">REMARKS</div>
          <div className="acord-section-body">
            <textarea className="acord-input" rows={4} value={form.remarks}
              onChange={e => set('remarks', e.target.value)}
              style={{ resize: 'vertical', minHeight: '3rem', padding: '0.4rem' }}
              placeholder="Additional remarks..." />
          </div>
        </div>
        {pageNav}
      </>}

      {/* PAGES 3..N — additional locations for insureds with >2 buildings.
          Each pair of locations gets its own 140 PDF at generation time. */}
      {currentPage > 2 && currentPage < signaturePage && <>
        {renderLocation(currentPage - 1, `LOCATION ${String.fromCharCode(64 + currentPage)}`)}
        {pageNav}
      </>}

      {/* SIGNATURE PAGE (always last) — applicant signature + date left blank;
          DocuSign routes live signature at signing time. */}
      {currentPage === signaturePage && <>
        <div className="acord-section">
          <div className="acord-section-bar">SIGNATURE</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">PRODUCER SIGNATURE</span>
                <input className="acord-input" value="Marc Fragos" readOnly style={{ fontStyle: 'italic' }} /></div>
              <div className="acord-cell"><span className="acord-label">DATE</span>
                <input className="acord-input" value={form.date} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">APPLICANT SIGNATURE</span>
                <input className="acord-input" value="" readOnly placeholder="— signed via DocuSign —" style={{ fontStyle: 'italic', color: '#888' }} /></div>
              <div className="acord-cell"><span className="acord-label">DATE</span>
                <input className="acord-input" value="" readOnly placeholder="— at signing —" style={{ color: '#888' }} /></div>
            </div>
          </div>
        </div>
        {pageNav}
      </>}

      {/* Actions */}
      <div className="acord-actions">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; Back to ACORDs</button>
        <button className="FIG-save-btn" onClick={handleSave} disabled={saving || !p.id}>
          {saving ? 'Saving...' : 'Save Coverage'}
        </button>
        {saveMsg && <span className={'FIG-save-msg' + (saveMsg.startsWith('Error') ? ' FIG-save-msg--error' : '')}>{saveMsg}</span>}
        <button className="FIG-save-btn acord-pdf-btn" disabled={generating} onClick={async () => {
          setGenerating(true);
          try {
            await generateACORD140(form, insured, getToken, p);
          } catch (err) {
            console.error('[ACORD 140] PDF generation failed:', err);
            alert('Error generating PDF: ' + err.message);
          } finally {
            setGenerating(false);
          }
        }}>
          {generating ? 'Generating...' : 'Generate PDF'}
        </button>
      </div>
    </div>
  );
}

export default ACORD140;
